
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ChevronLeftIcon from '../icons/ChevronLeftIcon';
import ChevronRightIcon from '../icons/ChevronRightIcon';
import { Add, Assignment, Person, ChevronRight, ChatBubbleOutline, FolderOpenOutlined, SettingsOutlined } from '@mui/icons-material';
import logoSrc from '../White_Logo.png';
import shortLogoSrc from '../engazeiq_logo.png'; // Assuming a shorter logo exists or we can use same and crop
import Historybtn from '../icons/History.png';
import { useNavigate } from 'react-router-dom';
import { deployedAgentsStreamUrl, fetchDeployedAgents, undeployAgent } from '../adk-agent-builder/api';

import { SessionMetadata } from "../utils/sessionManager";
import { useAuth } from '../context/AuthContext';

interface SidebarProps {
  collapsed: boolean;
  agents: string[];
  startSession: (agentInfo: string | { label: string; agent: string }) => Promise<void>;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  currentAgent?: string | null;
  sessions: SessionMetadata[];
  onLoadSession: (session: SessionMetadata) => Promise<void>;
  onDeleteSession: (session: SessionMetadata) => void;
  onUpdateSessionTitle: (sessionId: string, newTitle: string) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _AppItem = string;

const SidebarContainer = styled.div<{ collapsed: boolean }>`
  height: 100%;
  width: ${props => (props.collapsed ? '72px' : '260px')};
  background: #0f172a;
  color: #e2e8f0;
  transition: width 0.3s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-bottom: 8px;

  /* Custom Scrollbar Styles */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.2);
    }
  }
`;

const LogoIcon = styled.div<{ $collapsed?: boolean }>`
  width: ${props => props.$collapsed ? '40px' : '160px'};
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const LogoFrame = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.15);
`;

const LogoText = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #f8fafc;
  letter-spacing: -0.01em;
`;

const NewChatButton = styled.button`
  background: #2563eb;
  color: #ffffff;
  border: none;
  border-radius: 9999px;
  padding: 10px 16px;
  margin: 14px 16px 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4);
  transition: all 0.2s ease;
  width: calc(100% - 32px);
  &:hover {
    background: #3b82f6;
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
    transform: translateY(-1px);
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  padding: 6px 16px;
  margin-bottom: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  background-color: transparent;
  border: none;
  color: #64748b;
  text-align: left;
  cursor: pointer;
  transition: color 0.2s;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:hover {
    color: #94a3b8;
  }
`;

const ChatHistoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
  background: transparent;
`;

const ChatHistoryItem = styled.div<{ $active?: boolean }>`
  padding: 8px 12px;
  margin: 2px 12px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s;
  border-radius: 9999px;
  &:hover {
    background: rgba(30, 41, 59, 0.65);
  }
  &:active {
    background: #2563eb;
    box-shadow: 0 8px 18px rgba(37, 99, 235, 0.3);
  }
  ${({ $active }) =>
    $active &&
    `
      background: #1e293b;
      box-shadow: 0 0 15px rgba(59, 130, 246, 0.15);
      border: 1px solid rgba(59, 130, 246, 0.3);
    `}
`;

const ChatIcon = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
`;

const ChatTitle = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  color: #e2e8f0;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  visibility: hidden;
  transition: color 0.2s;
  &:hover {
    color: white;
  }
`;

const ChatItemContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  position: relative;
  &:hover ${EditButton} {
    visibility: visible;
  }
`;

const Menu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  background: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  min-width: 120px;
  z-index: 1000;
`;

const MenuItem = styled.button`
  background: none;
  border: none;
  color: #e2e8f0;
  text-align: left;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CollapseButton = styled.button`
  position: absolute;
  right: -12px;
  top: 20px;
  width: 24px;
  height: 24px;
  background-color: #0f172a;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  z-index: 100;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.35);
  transition: background-color 0.2s ease, border-color 0.2s ease;
  &:hover {
    background-color: #1e293b;
    border-color: rgba(148, 163, 184, 0.6);
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _UserProfile = styled.div`
  margin-top: auto;
  padding: 16px;
  display: flex;
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: #ccc;
  margin-right: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _UserName = styled.div`
  font-weight: 500;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _UserEmail = styled.div`
  font-size: 12px;
  opacity: 0.8;
`;

const CollapsedContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
  flex: 1;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s;
  &:hover {
    background-color: rgba(148, 163, 184, 0.16);
    color: #f8fafc;
  }
`;

const NoHistoryBox = styled(ChatHistoryItem)`
  justify-content: center;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin: 8px 16px;
  padding: 10px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.5);
  background-color: rgba(255, 255, 255, 0.03);
  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }
`;

const mockConversationsInit = [
  { session_id: '1', title: 'Session 1' },
  { session_id: '2', title: 'Session 2' },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, agents, startSession, setCollapsed, currentAgent, sessions, onLoadSession, onDeleteSession, onUpdateSessionTitle }) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [_customAgentsCollapsed, _setCustomAgentsCollapsed] = useState<boolean>(false);
  const [deployedAgents, setDeployedAgents] = useState<Array<{ id?: string; name: string; identifier: string; description: string }>>([]);
  const { user } = useAuth();

  const [selectedWorkspace, setSelectedWorkspace] = useState<any | null>(null);
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [userWorkspaces, setUserWorkspaces] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const handleWorkspaceUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail) {
        setSelectedWorkspace(customEvent.detail.selectedWorkspace);
        setWorkflows(customEvent.detail.workflows);
        if (customEvent.detail.userWorkspaces) {
          setUserWorkspaces(customEvent.detail.userWorkspaces);
        }
      }
    };
    window.addEventListener('workspaceUpdate', handleWorkspaceUpdate);
    return () => window.removeEventListener('workspaceUpdate', handleWorkspaceUpdate);
  }, []);

  const visibleAgents = React.useMemo(() => {
    const parseAgentIds = (rawAgentIds: unknown): string[] => {
      try {
        const parsed = typeof rawAgentIds === 'string' ? JSON.parse(rawAgentIds) : (rawAgentIds ?? []);
        return Array.isArray(parsed) ? parsed.map((id) => String(id)) : [];
      } catch {
        return Array.isArray(rawAgentIds) ? rawAgentIds.map((id) => String(id)) : [];
      }
    };

    const mapWorkflowIdsToIdentifiers = (workflowIds: string[]) => {
      const allowedIdentifiers = new Set<string>();

      workflowIds.forEach((workflowId) => {
        const normalizedWorkflowId = String(workflowId).toLowerCase();
        if (!normalizedWorkflowId) {
          return;
        }

        const deployedAgentMatch = deployedAgents.find(
          (agent) => String(agent.id ?? '').toLowerCase() === normalizedWorkflowId
        );
        if (deployedAgentMatch?.identifier) {
          allowedIdentifiers.add(deployedAgentMatch.identifier.toLowerCase());
          return;
        }

        const workflowMatch = workflows.find(
          (workflow) => String(workflow?.id ?? '').toLowerCase() === normalizedWorkflowId
        );
        if (workflowMatch?.identifier) {
          allowedIdentifiers.add(String(workflowMatch.identifier).toLowerCase());
          return;
        }

        allowedIdentifiers.add(normalizedWorkflowId);
      });

      return allowedIdentifiers;
    };

    const matchesAllowedAgent = (
      deployedAgent: { name: string; identifier: string },
      allowedIdentifiers: Set<string>
    ) => {
      const identifier = deployedAgent.identifier?.toLowerCase?.() ?? '';
      const name = deployedAgent.name?.toLowerCase?.() ?? '';
      return allowedIdentifiers.has(identifier) || allowedIdentifiers.has(name);
    };

    if (user?.role?.toLowerCase() === 'super admin') {
      return deployedAgents;
    }

    if (user?.role?.toLowerCase() === 'org admin') {
      // Backend /workflows already applies org-admin access rules.
      const workflowScopedIdentifiers = new Set<string>(
        workflows
          .map((workflow) => String(workflow?.identifier ?? '').toLowerCase())
          .filter(Boolean)
      );

      if (workflowScopedIdentifiers.size > 0) {
        return deployedAgents.filter((agent) => matchesAllowedAgent(agent, workflowScopedIdentifiers));
      }

      const workspaceAgentIds: string[] = [];
      userWorkspaces.forEach((ws) => {
        workspaceAgentIds.push(...parseAgentIds(ws.agentIds));
      });

      const workspaceScopedIdentifiers = mapWorkflowIdsToIdentifiers(workspaceAgentIds);
      return deployedAgents.filter((agent) => matchesAllowedAgent(agent, workspaceScopedIdentifiers));
    }

    if (!selectedWorkspace) return [];

    const workspaceAgentIds = parseAgentIds(selectedWorkspace.agentIds);
    if (workspaceAgentIds.length === 0) return [];

    const allowedIdentifiers = mapWorkflowIdsToIdentifiers(workspaceAgentIds);
    return deployedAgents.filter((agent) => matchesAllowedAgent(agent, allowedIdentifiers));
  }, [user?.role, deployedAgents, selectedWorkspace, workflows, userWorkspaces]);

  // Refresh deployed agents periodically (in case user deployed from Agent Builder)
  useEffect(() => {
    let isMounted = true;
    let eventSource: EventSource | null = null;

    const load = async () => {
      try {
        const agents = await fetchDeployedAgents();
        if (isMounted) {
          setDeployedAgents(agents);
        }
      } catch {
        // Ignore refresh errors.
      }
    };

    load();
    eventSource = new EventSource(deployedAgentsStreamUrl);
    eventSource.onmessage = () => {
      load();
    };
    eventSource.onerror = () => {
      // Keep the last known list if the stream drops.
    };

    return () => {
      isMounted = false;
      if (eventSource) {
        eventSource.close();
      }
    };
  }, []);

  const handleEdit = (id: string, currentTitle: string) => {
    setEditingId(id);
    setEditValue(currentTitle);
    setMenuOpenId(null);
  };

  const handleSave = (id: string) => {
    onUpdateSessionTitle(id, editValue);
    setEditingId(null);
  };

  const handleDelete = (session: SessionMetadata) => {
    onDeleteSession(session);
    setMenuOpenId(null);
  };

  const handleLoadSession = async (session: SessionMetadata) => {
    await onLoadSession(session);
  };

  return (
    <SidebarContainer collapsed={collapsed}>
      <CollapseButton onClick={() => setCollapsed(prev => !prev)}>
        {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </CollapseButton>

      {collapsed ? (
        <>
          <CollapsedContent>
            <LogoIcon $collapsed={true} style={{ justifyContent: 'flex-start', paddingLeft: '6px' }}>
              <img
                src={shortLogoSrc}
                alt="EngazeIQ"
                style={{ height: "28px", width: "130px", objectFit: "cover", objectPosition: "left center", maxWidth: "none", marginLeft: "-4px" }}
              />
            </LogoIcon>
            <IconButton style={{ marginTop: '16px' }} onClick={() => startSession(currentAgent || "base_agent")}>
              <Add style={{ fontSize: '20px', color: '#2563eb' }} />
            </IconButton>
            <IconButton><ChatBubbleOutline style={{ fontSize: '20px' }} /></IconButton>
            <IconButton onClick={() => window.location.href = '/admin?view=workspaces'}><FolderOpenOutlined style={{ fontSize: '20px' }} /></IconButton>
            <IconButton onClick={() => navigate('/admin')}><SettingsOutlined style={{ fontSize: '20px' }} /></IconButton>
          </CollapsedContent>

          {/* Collapsed user profile (icon only) */}
          <IconButton style={{ marginBottom: '16px' }}>
            <Person />
          </IconButton>
        </>
      ) : (
        <>
          <LogoFrame>
            <LogoIcon>
              <img
                src={logoSrc}
                alt="EngazeIQ"
                style={{ height: "30px", width: "auto", objectFit: "contain" }}
              />
            </LogoIcon>
          </LogoFrame>
          <NewChatButton onClick={() => startSession(currentAgent || "base_agent")}>
            <Add style={{ fontSize: '18px', marginRight: '6px', color: '#ffffff' }} />
            New Conversation
          </NewChatButton>
          <ScrollableContent>

            <Section>
              <SectionTitle>
                <span>Conversations History</span>
                <ChevronRight style={{ width: 16, height: 16, color: '#64748b' }} />
              </SectionTitle>
              <ChatHistoryList>
                {sessions.length > 0 ? (
                  sessions.slice(0, 5).map(session => (
                    <ChatHistoryItem
                      key={session.session_id}
                      onClick={() => handleLoadSession(session)}
                      $active={false}
                    >
                      <ChatIcon>
                        <Assignment style={{ width: '18px', height: '18px', color: '#94a3b8' }} />
                      </ChatIcon>
                      <ChatItemContainer>
                        {editingId === session.session_id ? (
                          <input
                            value={editValue}
                            autoFocus
                            onClick={e => e.stopPropagation()}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={() => handleSave(session.session_id)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSave(session.session_id);
                            }}
                            style={{
                              flex: 1,
                              background: 'transparent',
                              border: '1px solid #fff',
                              color: '#fff',
                              padding: '2px 4px',
                              borderRadius: '4px',
                            }}
                          />
                        ) : (
                          <>
                            <ChatTitle>{session.title}</ChatTitle>
                            <EditButton
                              title="Options"
                              onClick={e => {
                                e.stopPropagation();
                                setMenuOpenId(menuOpenId === session.session_id ? null : session.session_id);
                              }}
                            >
                              ⋮
                            </EditButton>
                            {menuOpenId === session.session_id && (
                              <Menu>
                                <MenuItem onClick={() => handleEdit(session.session_id, session.title)}>✏️ Edit</MenuItem>
                                <MenuItem onClick={() => handleDelete(session)}>🗑️ Delete</MenuItem>
                              </Menu>
                            )}
                          </>
                        )}
                      </ChatItemContainer>
                    </ChatHistoryItem>
                  ))
                ) : (
                  <NoHistoryBox>
                    <img
                      src={Historybtn}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        marginRight: '20px',
                      }}
                      alt="history"
                    />
                    No Conversation History
                  </NoHistoryBox>
                )}
              </ChatHistoryList>
            </Section>

            <Section style={{ marginTop: '24px' }}>
              <SectionTitle>
                <span>Available Agents</span>
              </SectionTitle>
              {/* Individual Deployed Agents - shown directly like other agents */}
              {visibleAgents.map(agent => (
                <ChatHistoryItem
                  key={agent.identifier}
                  onClick={() => startSession({ label: agent.name, agent: agent.identifier })}
                  title={agent.description || agent.name}
                  $active={currentAgent === agent.identifier}
                >
                  <ChatIcon>
                    <Person style={{ width: '20px', height: '20px', color: currentAgent === agent.identifier ? '#8b5cf6' : 'rgba(139, 92, 246, 0.7)' }} />
                  </ChatIcon>
                  <ChatTitle style={{ flex: 1, color: '#e2e8f0', fontWeight: currentAgent === agent.identifier ? 600 : 400 }}>{agent.name.replace(/_/g, ' ')}</ChatTitle>
                </ChatHistoryItem>
              ))}
            </Section>

          </ScrollableContent>

          {/* Expanded user profile */}

        </>
      )
      }
    </SidebarContainer >
  );
};

export default Sidebar;
